import { useEffect, useRef, useState } from 'react'
import { bridge } from '../../../bridge'
import { sendRelayInput } from '../../../api/ws'
import { useAppStore } from '../../../store/app'
import {
  handsFreeAvailable,
  probeVoiceCapabilities,
  spokenEntryIDs,
  unspokenEntries,
  voiceSessionOffered,
} from '../voiceSession'
import './VoiceSessionBar.css'

/**
 * The hands-free voice session, minus the hands-free part — for now.
 *
 * What this gives you today: put the phone down, tap the mic as usual, and hear
 * the agent's answer out loud instead of reading it. What step 4 adds is the
 * turn detection that removes the tapping.
 *
 * Two rules it exists to enforce:
 *
 *   1. Only speak what arrives DURING the session. Opening voice mode on an
 *      agent you have been chatting with all morning must not read the morning
 *      back to you, so the session seeds itself with everything already said.
 *   2. Never leave the screen pinned on. The keep-awake is released on every
 *      exit path here, and both shells drop it again when the app backgrounds,
 *      because a swipe-away is not a session end.
 *
 * See docs/phone-voice-endpoint-plan.md §7 step 3.
 */
export function VoiceSessionBar({ agentInstanceID }: { agentInstanceID: string }) {
  const active =
    useAppStore((s) => s.voiceSessionAgentID) === agentInstanceID
  const setVoiceSessionAgent = useAppStore((s) => s.setVoiceSessionAgent)
  const entries = useAppStore(
    (s) => s.transcripts[agentInstanceID] ?? emptyEntries,
  )

  const [offered, setOffered] = useState(false)
  const [handsFree, setHandsFree] = useState(false)
  const [speaking, setSpeaking] = useState(false)
  const [micState, setMicState] = useState<'listening' | 'paused' | 'error'>('listening')
  const [micError, setMicError] = useState<string | null>(null)
  // Ids already voiced in this session. A ref, not state: mutating it must not
  // re-render, and it has to be readable synchronously inside the effect that
  // updates it.
  const spokenRef = useRef<Set<string>>(new Set())

  // Probe once per mount. A shell that predates voice.* rejects, and the
  // control simply never appears — the same "hide, don't disable" rule the mic
  // button follows.
  useEffect(() => {
    let cancelled = false
    void probeVoiceCapabilities().then((caps) => {
      if (cancelled) return
      setOffered(voiceSessionOffered(caps))
      setHandsFree(handsFreeAvailable(caps))
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Speak what arrives while the session is open.
  useEffect(() => {
    if (!active) return
    const pending = unspokenEntries(entries, spokenRef.current)
    if (pending.length === 0) return
    for (const e of pending) spokenRef.current.add(e.id)
    // Speak only the newest. voice.speak interrupts anyway, and reading a
    // backlog aloud to someone who just walked back into the kitchen helps
    // nobody — the transcript still has every word.
    const latest = pending[pending.length - 1]
    setSpeaking(true)
    void bridge
      .call('voice.speak', { text: latest.voice!.spoken })
      .catch(() => {})
      .finally(() => setSpeaking(false))
  }, [active, entries])

  // Release the keep-awake if this screen goes away mid-session. The store flag
  // is left alone on purpose: navigating away from an agent should not silently
  // end a session you started, and coming back re-arms the hold.
  useEffect(() => {
    if (!active) return
    void bridge.call('voice.setKeepAwake', { enabled: true }).catch(() => {})
    return () => {
      void bridge.call('voice.setKeepAwake', { enabled: false }).catch(() => {})
      void bridge.call('voice.stopSpeaking').catch(() => {})
    }
  }, [active])

  // Hands-free capture. The shell listens continuously and tells us when a turn
  // ended; we forward it as a spoken message. Nothing here decides WHEN a turn
  // ends — that is the shell's job, by whatever means it actually has (§2).
  useEffect(() => {
    if (!active || !handsFree) return

    const offTurn = bridge.on('voice.turn', ({ text }) => {
      const said = text.trim()
      if (!said) return
      // `spoken: true` is what makes the server apply the voice preamble, and
      // therefore what makes the agent answer aloud rather than in writing.
      sendRelayInput(agentInstanceID, said, undefined, undefined, true)
    })
    const offState = bridge.on('voice.state', ({ state, reason }) => {
      setMicState(state)
      setMicError(state === 'error' ? (reason ?? 'unknown') : null)
    })

    setMicState('listening')
    setMicError(null)
    void bridge.call('voice.startSession').then(({ started }) => {
      // A shell that refuses (no provable on-device recognition) leaves the
      // session running as tap-to-talk rather than pretending to listen.
      if (!started) setHandsFree(false)
    }).catch(() => setHandsFree(false))

    return () => {
      offTurn()
      offState()
      void bridge.call('voice.endSession').catch(() => {})
    }
  }, [active, handsFree, agentInstanceID])

  if (!offered) return null

  async function start() {
    // Seed with what has already been said, so the session speaks only what
    // comes next.
    spokenRef.current = spokenEntryIDs(entries)

    // Re-probe rather than trusting the answer from mount. On iOS `session` is
    // false until the Whisper model is downloaded AND loaded, and the usual way
    // someone downloads it is by tapping the mic on this very screen — so the
    // mount-time answer is stale exactly when it matters most, and hands-free
    // would stay off until the user happened to navigate away and back. That
    // reads as "voice mode is broken" on a first try.
    //
    // Asking here rather than watching for a download-finished signal also
    // covers every other way capabilities can change under a long-lived screen,
    // and needs no plumbing between this and the mic button.
    setHandsFree(handsFreeAvailable(await probeVoiceCapabilities()))
    setVoiceSessionAgent(agentInstanceID)
  }

  function stop() {
    setVoiceSessionAgent(null)
    void bridge.call('voice.stopSpeaking').catch(() => {})
    void bridge.call('voice.setKeepAwake', { enabled: false }).catch(() => {})
    void bridge.call('voice.endSession').catch(() => {})
  }

  function statusLabel(): string {
    if (speaking) return 'Speaking…'
    if (!handsFree) return 'Voice mode — replies play aloud'
    if (micState === 'error') return `Microphone stopped (${micError})`
    if (micState === 'paused') return 'Listening paused while speaking'
    return 'Listening — just talk'
  }

  if (!active) {
    return (
      <button
        className="voicebar__start"
        onClick={() => void start()}
        aria-label="Start voice mode"
      >
        <span aria-hidden="true">🔊</span> Voice mode
      </button>
    )
  }

  return (
    <div className="voicebar" role="status">
      <span
        className={`voicebar__dot${micState === 'error' ? ' voicebar__dot--error' : ''}`}
        aria-hidden="true"
      />
      <span className="voicebar__label">{statusLabel()}</span>
      <button className="voicebar__stop" onClick={stop} aria-label="End voice mode">
        End
      </button>
    </div>
  )
}

// Stable identity so the selector doesn't produce a new array every render.
const emptyEntries: never[] = []
